import type { BDirectRow } from '../../hooks/useBDirectRows';

// BDirect release tag conventions:
//   UAT  → `<XXXX>.0-rc<N>`     e.g. `2604.0-rc1`, `2604.0-rc2`
//   PROD → `<XXXX>.0.<Y>-prod`  e.g. `2604.0.0-prod`, `2604.0.1-prod`
//
// The "major release" for both is the leading `<XXXX>.0` — everything
// after that is RC number, patch digit, or environment suffix. Extract
// that pair so rc-vs-rc and patch-vs-patch comparisons within the same
// major all collapse to the same key.
export const majorReleasePrefix = (tag: string): string | null => {
  const m = /^(\d+\.\d+)/.exec(tag);
  return m ? m[1] : null;
};

// Two release tags share the same major release when their leading
// `<XXXX>.0` prefix matches. Used to relax the readiness check so a row
// whose region is still live on a sibling tag in the same major (rc1 vs
// rc2, or prod.0 vs prod.1) counts as deployed — the rc-rolling and
// prod-patch cases where the new release's pipeline may be stuck but the
// previous tag is still serving traffic. Tags that don't match the
// `<digits>.<digits>` prefix shape fall back to strict equality.
export const sameMajorRelease = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  const aMajor = majorReleasePrefix(a);
  const bMajor = majorReleasePrefix(b);
  if (!aMajor || !bMajor) return false;
  return aMajor === bMajor;
};

// Tint key for parked rows. `undefined` = not parked, so no tint.
export type ParkedTint = 'not_ready' | 'no_changes' | undefined;

// Display-level derivations the BDirect table needs per row across both
// the screen card and the print page:
//   - `isParked`        — collapse Build + region cells into a single
//                         "parked" message.
//   - `tint`            — grey shade applied to the row when parked.
//   - `needsAttention`  — user marked the row `no_changes` but the repo
//                         carries a same-major Bitbucket tag — the
//                         deployment will silently skip a tagged repo
//                         unless the user flips state back. Overrides
//                         `tint` with red wherever both surfaces render.
//   - `parkedMessage`   — text shown in the collapsed parked cell. When
//                         `needsAttention` is true the message names the
//                         offending tag so the user can act on it.
export interface RowDisplayMeta {
  isParked: boolean;
  tint: ParkedTint;
  needsAttention: boolean;
  parkedMessage: string;
}

export const rowDisplayMeta = (
  row: BDirectRow,
  release: string,
): RowDisplayMeta => {
  const parkedState =
    row.state === 'not_ready' || row.state === 'no_changes'
      ? row.state
      : undefined;
  const isParked = !!row.repo && parkedState !== undefined;
  const tint: ParkedTint = isParked ? parkedState : undefined;
  const needsAttention =
    !!row.repo &&
    row.state === 'no_changes' &&
    !!row.latestTag &&
    sameMajorRelease(row.latestTag, release);
  const parkedMessage = needsAttention
    ? `Marked "No changes" but ${row.latestTag} exists in Bitbucket`
    : row.state === 'not_ready'
    ? 'Awaiting tagging'
    : 'No changes for this release';
  return { isParked, tint, needsAttention, parkedMessage };
};
